import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Loader2, Eye, EyeOff, ArrowRight, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  // Canvas particle system
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];
    let mouse = { x: -999, y: -999 };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const handleMouse = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
    window.addEventListener('mousemove', handleMouse);

    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        this.opacity = Math.random() * 0.5 + 0.1;
        this.hue = Math.random() > 0.5 ? 0 : 25; // red or orange
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        // Mouse repulsion
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120) {
          this.x -= dx * 0.02;
          this.y -= dy * 0.02;
        }
        if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) this.reset();
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, 85%, 55%, ${this.opacity})`;
        ctx.fill();
        ctx.shadowColor = `hsla(${this.hue}, 85%, 55%, 0.5)`;
        ctx.shadowBlur = this.size * 4;
      }
    }

    for (let i = 0; i < 80; i++) particles.push(new Particle());

    const connectParticles = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(230, 57, 70, ${0.08 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.shadowBlur = 0;
      particles.forEach(p => { p.update(); p.draw(); });
      connectParticles();
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouse);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      // Clear session flags so popups show fresh on new login
      sessionStorage.removeItem('quote_shown_this_session');
      if (response.data.user.must_change_password) {
        toast.info('Sila tukar password anda untuk meneruskan');
        navigate('/change-password', { state: { firstLogin: true } });
      } else {
        toast.success('Login berjaya!');
        navigate('/my-scorecard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email atau password tidak sah');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Link reset password telah dihantar ke email anda');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Gagal menghantar email reset');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#050505]">
      {/* Interactive Canvas Background */}
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />

      {/* Ambient Glow Orbs */}
      <div className="absolute inset-0 z-[1] pointer-events-none">
        <div className="absolute top-[-15%] left-[20%] w-[500px] h-[500px] rounded-full bg-[#E63946]/12 blur-[150px]" style={{ animation: 'pulseOrb 8s ease-in-out infinite' }} />
        <div className="absolute bottom-[-20%] right-[15%] w-[600px] h-[600px] rounded-full bg-[#F97316]/10 blur-[180px]" style={{ animation: 'pulseOrb 10s ease-in-out infinite 3s' }} />
        <div className="absolute top-[50%] left-[-5%] w-[350px] h-[350px] rounded-full bg-[#DC2626]/8 blur-[120px]" style={{ animation: 'pulseOrb 7s ease-in-out infinite 1.5s' }} />
      </div>

      {/* Scan lines overlay */}
      <div className="absolute inset-0 z-[2] pointer-events-none opacity-[0.03]"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)' }}
      />

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-[420px]" style={{ animation: 'fadeSlideUp 0.8s ease-out' }}>

          {/* Logo & Branding */}
          <div className="text-center mb-12 space-y-6">
            <div className="relative inline-block" style={{ animation: 'logoReveal 1s ease-out 0.2s both' }}>
              {/* Multi-layered glow */}
              <div className="absolute inset-[-30px] blur-[60px] bg-[#E63946]/25 rounded-full" style={{ animation: 'pulseOrb 4s ease-in-out infinite' }} />
              <div className="absolute inset-[-15px] blur-[30px] bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 rounded-full" />
              <img
                src="https://customer-assets.emergentagent.com/job_channel-crm-system/artifacts/4egfgzp6_ua%20logo.png"
                alt="Underads Logo"
                className="relative h-32 md:h-40 mx-auto drop-shadow-[0_0_30px_rgba(230,57,70,0.4)] hover:scale-105 transition-transform duration-700"
                data-testid="underads-logo"
              />
            </div>

            <div className="space-y-3" style={{ animation: 'fadeSlideUp 0.8s ease-out 0.4s both' }}>
              <h1 className="text-2xl md:text-3xl font-bold text-white/90 tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>
                Underads <span className="bg-gradient-to-r from-[#E63946] to-[#F97316] bg-clip-text text-transparent">CRM</span>
              </h1>
              <div className="flex items-center justify-center gap-3 px-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#E63946]/40 to-transparent" />
                <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#E63946]/80 whitespace-nowrap">
                  #1 High-End Tech Management Agency
                </p>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#E63946]/40 to-transparent" />
              </div>
            </div>
          </div>

          {/* Login Card */}
          <div className="relative" style={{ animation: 'fadeSlideUp 0.8s ease-out 0.6s both' }}>
            {/* Animated border */}
            <div className="absolute -inset-[1px] rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-[#E63946] via-[#F97316] to-[#E63946] opacity-60" style={{ animation: 'borderShift 4s linear infinite', backgroundSize: '200% 100%' }} />
            </div>

            <div className="relative bg-[#0a0a0a]/90 backdrop-blur-2xl rounded-2xl p-8 border border-white/[0.05]">
              {showForgotPassword ? (
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-semibold text-white mb-2">Lupa Password?</h2>
                    <p className="text-sm text-gray-500">Masukkan email anda untuk reset password</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-2">
                      <Mail size={11} className="text-[#E63946]" /> Email
                    </Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="bg-white/[0.04] border-white/[0.08] focus:border-[#E63946]/60 focus:ring-[#E63946]/15 text-white rounded-xl h-12 transition-all duration-300"
                      placeholder="email@example.com"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full h-12 bg-gradient-to-r from-[#E63946] to-[#F97316] hover:from-[#DC2626] hover:to-[#EA580C] text-white font-semibold rounded-xl shadow-[0_4px_24px_-4px_rgba(230,57,70,0.4)] transition-all duration-300 hover:shadow-[0_4px_32px_-4px_rgba(230,57,70,0.6)] hover:scale-[1.01]"
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Hantar Link Reset'}
                  </Button>
                  <button type="button" onClick={() => setShowForgotPassword(false)}
                    className="w-full text-center text-sm text-[#E63946]/70 hover:text-[#E63946] transition-colors">
                    Kembali ke Login
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="text-center mb-2">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/[0.06] bg-white/[0.02]">
                      <Zap size={13} className="text-[#F97316]" />
                      <span className="text-[11px] font-medium text-white/50 tracking-wide">Selamat Datang</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-2">
                      <Mail size={11} className="text-[#E63946]" /> Email
                    </Label>
                    <div className="relative group">
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="bg-white/[0.04] border-white/[0.08] focus:border-[#E63946]/60 focus:ring-[#E63946]/15 text-white rounded-xl h-12 transition-all duration-300 group-focus-within:bg-white/[0.06]"
                        placeholder="email@underads.com"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-2">
                      <Lock size={11} className="text-[#E63946]" /> Password
                    </Label>
                    <div className="relative group">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="bg-white/[0.04] border-white/[0.08] focus:border-[#E63946]/60 focus:ring-[#E63946]/15 text-white rounded-xl h-12 pr-12 transition-all duration-300 group-focus-within:bg-white/[0.06]"
                        placeholder="••••••••"
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-[#E63946] transition-colors"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button type="button" onClick={() => setShowForgotPassword(true)}
                      className="text-xs text-gray-600 hover:text-[#E63946] transition-colors">
                      Lupa password?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-[#E63946] to-[#F97316] hover:from-[#DC2626] hover:to-[#EA580C] text-white font-semibold rounded-xl shadow-[0_4px_24px_-4px_rgba(230,57,70,0.4)] transition-all duration-300 hover:shadow-[0_4px_32px_-4px_rgba(230,57,70,0.6)] hover:scale-[1.01] group"
                    data-testid="login-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Log Masuk
                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 text-center" style={{ animation: 'fadeSlideUp 0.8s ease-out 0.8s both' }}>
            <p className="text-[11px] text-gray-700">
              &copy; 2026 Underads Capital. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulseOrb {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes logoReveal {
          from { opacity: 0; transform: scale(0.85); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes borderShift {
          0% { background-position: 0% 0%; }
          100% { background-position: 200% 0%; }
        }
      `}</style>
    </div>
  );
}
